import React, { Component } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Sticky from 'react-stickynode';
import { selectShowCurrencySelection } from 'tradera-state/multi-currency/selectors';
import { withIsMobile } from 'tradera-hooks/use-is-mobile';
import { withIsTablet } from 'tradera-hooks/use-is-tablet';
import { withIsDesktop } from 'tradera-hooks/use-is-desktop';
import { HOVER_MENUS } from './hover-menu/constants';
import cookieUtil from 'tradera-utils/cookie';
import { onWindowLoad } from 'utils/on-ready';
import { actions as memberStateActions } from 'tradera-state/member';
import uiActions from 'tradera-state/ui/actions';
import { withUrlLocalizer } from 'tradera-lang/translate';
import TraderaLogo from 'components/tradera/logo?jsx';
import TraderaAppLogo from '@tradera/blueprint/theme/logos/tradera-app-logo.svg?jsx';
import CartLink from 'tradera-components/site-header/cart-link';
import DropdownMenu from 'tradera-components/site-header/dropdown-menu';
import SignUpLink from 'tradera-components/site-header/sign-up-link';
import ALink from 'tradera-components/alink/alink';
import { SearchInSpa, SearchOutsideSpa } from 'components/search/search';
import ModalPortal from 'tradera-components/portals/modal-portal';
import PushNotificationModal from 'tradera-components/push-notification-modal/push-notification-modal';
import { LocalizationLink } from './localization-link';
import BuyingLink from './buying-link';
import { HowTraderaWorks } from './how-tradera-works';
import NewListingLink from './new-listing-link';
import ProfileMenuLink from './profile-menu-link';
import { SearchButton } from './search-button';

import MessagingModal from 'tradera-components/messaging/messaging-modal';
import WishlistLink from 'tradera-components/site-header/wishlist-link';
import OngoingAuctionsLink from 'tradera-components/site-header/ongoing-auctions-link';
import SellingLink from 'tradera-components/site-header/selling-link';
import { TraderaToastContainer } from 'tradera-components/toasts/tradera-toast-container';
import { CurrencyLink } from 'tradera-components/site-header/currency-link';

class SiteHeader extends Component {
    static propTypes = {
        cartItemCount: PropTypes.number,
        cartSum: PropTypes.number,
        closeMessagingModal: PropTypes.func,
        fetchMessagingNotifications: PropTypes.func,
        getGeolocation: PropTypes.func,
        inSPA: PropTypes.bool,
        searchQuery: PropTypes.string,
        showHeaderSearch: PropTypes.bool
    };

    static defaultProps = {
        inSPA: false
    };

    constructor(props) {
        super(props);
        this.state = {
            sticky: false,
            autoFocusSearch: false
        };
    }

    componentDidMount() {
        const {
            featureSwitches,
            isLoggedIn,
            fetchMessagingNotifications,
            getGeolocation
        } = this.props;
        getGeolocation();

        if (featureSwitches['messaging'] && isLoggedIn) {
            fetchMessagingNotifications();
        }

        // Scroll up one pixel to show fixed header on mobile
        onWindowLoad(() => {
            // Toggle on sticky behavior on window load to force reflow and show sticky header on mobile & Firefox
            this.setState({ sticky: true });
        });
    }

    handleClickSearch = () => {
        const { openSearchForm, closeSearchForm, showSearch } = this.props;
        if (showSearch) {
            closeSearchForm();
        } else {
            openSearchForm();
        }
        this.setState(prevState => ({
            ...prevState,
            autoFocusSearch: !prevState.showSearch
        }));
    };

    handleMouseOverMenu = menu => {
        if (this.props.isDesktop) {
            this.setState({
                activeHoverMenu: menu
            });
        }
    };

    handleMouseOutMenu = () => {
        this.setState({
            activeHoverMenu: false
        });
    };

    // Event handler for menu button:
    // Mobile: link, no menu open
    // Tablet: not a link, menu open
    // Desktop: link, menu open
    handleMenuClick = (event, menu) => {
        if (
            this.props.isMobile &&
            menu !== HOVER_MENUS.LOCALIZATION &&
            menu !== HOVER_MENUS.CURRENCY
        ) {
            return;
        }

        // Prevent click for tablet to allow tap to open
        if (this.props.isTablet) {
            event.preventDefault();
        }
        // Force focus so touch devices can get a blur event
        event.target.focus();
        this.setState(state => ({
            activeHoverMenu: state.activeHoverMenu === menu ? false : menu
        }));
    };

    // Event handler for non-linked menu button that always open a menu
    handleAlwaysOpenMenuClick = (event, menu) => {
        event.preventDefault();
        event.target.focus();
        this.setState(state => ({
            activeHoverMenu: state.activeHoverMenu === menu ? false : menu
        }));
    };

    handleMenuBlur = () => {
        this.setState({
            activeHoverMenu: false
        });
    };

    handleRegisterClick = () => {
        const { isLoggedIn } = this.props;

        if (!isLoggedIn) {
            const cookieName = 'signup_referral_path';
            const expireDate = new Date();
            const value = window.location.href;
            expireDate.setTime(expireDate.getTime() + 10 * 60 * 1000);
            cookieUtil.createCookie(cookieName, value, expireDate);
        }
    };

    render() {
        const {
            cartItemCount,
            cartSum,
            closeMessagingModal,
            closeNotificationOptinModal,
            isMobile,
            inSPA,
            isLoggedIn,
            messagingModal,
            notificationOptinModal,
            pageName,
            showSearch,
            showCurrencySelection,
            showHeaderSearch,
            toLocalizedUrl
        } = this.props;
        const { sticky } = this.state;
        const formattedCartItems = cartItemCount;
        const hasCartItems = formattedCartItems > 0;
        const stickyEnabledOnPage = !pageName?.includes('MyTradera');

        const hideElements =
            !showHeaderSearch && pageName?.includes('StartPage');
        const transparentHeader = hideElements && !isMobile;

        return (
            <Sticky
                enabled={stickyEnabledOnPage}
                top={0}
                enableTransforms={sticky}
                activeClass="slim-header--sticky">
                <header
                    className={`slim-header d-hybrid-none container-fluid ${
                        showHeaderSearch ? '' : 'hide-elements'
                    }`}
                    id="site-header">
                    <div
                        className="row align-items-center mx-auto slim-header__content"
                        role="banner">
                        <DropdownMenu
                            className="navbar order-1 z-modal"
                            inSPA={inSPA}
                            isLoggedIn={isLoggedIn}
                            transparentHeader={transparentHeader}
                            tabIndex={1}
                        />
                        <div
                            className="slim-header__logo order-2 mx-1"
                            id="slim-header__logo">
                            <ALink
                                className="d-flex"
                                useLink={inSPA}
                                aria-label="Home"
                                href={toLocalizedUrl('/')}>
                                <TraderaAppLogo
                                    className="d-md-none"
                                    width="25"
                                    height="25"
                                />
                                <TraderaLogo
                                    style={{
                                        height: '14px'
                                    }}
                                    className={`d-none d-md-block ${
                                        hasCartItems ? 'has-cart-items' : ''
                                    }`}
                                />
                            </ALink>
                        </div>
                        <div
                            className={`col-12 order-4 order-md-3 col-md-auto d-none d-md-flex mt-2 mt-md-0 px-1 ${
                                showSearch && !hideElements ? 'd-flex' : ''
                            } slim-header__search `}
                            id="slim-header__search">
                            {inSPA ? (
                                <SearchInSpa inSPA={inSPA} />
                            ) : (
                                <SearchOutsideSpa inSPA={inSPA} />
                            )}
                        </div>
                        <nav
                            className="col col-md-auto order-3 order-md-4 navigation-links"
                            role="navigation">
                            <ul className="plain-list row ml-n1 justify-content-end flex-nowrap">
                                <li className="slim-header__menu-option slim-header__menu-option-search d-flex d-md-none align-items-center">
                                    <SearchButton
                                        showSearch={showSearch}
                                        onClick={this.handleClickSearch}
                                    />
                                </li>
                                {!isLoggedIn && (
                                    <li className="slim-header__menu-option z-modal d-flex align-items-center">
                                        <LocalizationLink
                                            onClick={this.handleMenuClick}
                                            mouseOver={this.handleMouseOverMenu}
                                            mouseOut={this.handleMouseOutMenu}
                                            onBlur={this.handleMenuBlur}
                                            showMenu={
                                                this.state.activeHoverMenu ===
                                                HOVER_MENUS.LOCALIZATION
                                            }
                                            transparentHeader={
                                                transparentHeader
                                            }
                                        />
                                    </li>
                                )}
                                {!isLoggedIn && showCurrencySelection && (
                                    <li className="slim-header__menu-option z-modal d-flex align-items-center">
                                        <CurrencyLink
                                            onClick={this.handleMenuClick}
                                            mouseOver={this.handleMouseOverMenu}
                                            mouseOut={this.handleMouseOutMenu}
                                            onBlur={this.handleMenuBlur}
                                            showMenu={
                                                this.state.activeHoverMenu ===
                                                HOVER_MENUS.CURRENCY
                                            }
                                            transparentHeader={
                                                transparentHeader
                                            }
                                        />
                                    </li>
                                )}
                                <li className="d-none d-md-flex slim-header__menu-option z-modal align-items-center">
                                    <NewListingLink
                                        transparentHeader={transparentHeader}
                                    />
                                </li>
                                {isLoggedIn && (
                                    <li
                                        className={`slim-header__menu-option z-header align-items-center d-md-none ${
                                            hasCartItems
                                                ? 'd-none d-sm-flex'
                                                : 'd-flex'
                                        }`}>
                                        <OngoingAuctionsLink />
                                    </li>
                                )}
                                <li className="slim-header__menu-option z-modal d-flex align-items-center">
                                    {isLoggedIn ? (
                                        <WishlistLink
                                            onClick={this.handleMenuClick}
                                            mouseOver={this.handleMouseOverMenu}
                                            mouseOut={this.handleMouseOutMenu}
                                            onBlur={this.handleMenuBlur}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                            showMenu={
                                                this.state.activeHoverMenu ===
                                                HOVER_MENUS.WISHLIST
                                            }
                                        />
                                    ) : (
                                        <HowTraderaWorks
                                            mouseOver={this.handleMouseOverMenu}
                                            mouseOut={this.handleMouseOutMenu}
                                            onBlur={this.handleMenuBlur}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                            showMenu={
                                                this.state.activeHoverMenu ===
                                                HOVER_MENUS.HOW_TRADERA_WORKS
                                            }
                                        />
                                    )}
                                </li>
                                {isLoggedIn && (
                                    <li className="slim-header__menu-option z-modal d-none d-md-flex align-items-center">
                                        <BuyingLink
                                            onClick={this.handleMenuClick}
                                            mouseOver={this.handleMouseOverMenu}
                                            mouseOut={this.handleMouseOutMenu}
                                            onBlur={this.handleMenuBlur}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                            showMenu={
                                                this.state.activeHoverMenu ===
                                                HOVER_MENUS.BUY
                                            }
                                        />
                                    </li>
                                )}
                                {isLoggedIn && (
                                    <li className="slim-header__menu-option z-modal d-none d-md-flex align-items-center">
                                        <SellingLink
                                            onClick={this.handleMenuClick}
                                            mouseOver={this.handleMouseOverMenu}
                                            mouseOut={this.handleMouseOutMenu}
                                            onBlur={this.handleMenuBlur}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                            showMenu={
                                                this.state.activeHoverMenu ===
                                                HOVER_MENUS.SELL
                                            }
                                        />
                                    </li>
                                )}

                                {hasCartItems && (
                                    <li className="slim-header__menu-option z-modal d-flex align-items-center">
                                        <CartLink
                                            formattedCartItems={
                                                formattedCartItems
                                            }
                                            cartSum={cartSum}
                                            transparentHeader={
                                                transparentHeader
                                            }
                                        />
                                    </li>
                                )}
                                <li className="slim-header__menu-option z-header d-flex align-items-center">
                                    <ProfileMenuLink
                                        transparentHeader={transparentHeader}
                                    />
                                </li>
                                {!isLoggedIn && (
                                    <li className="slim-header__menu-option z-header d-none d-lg-flex align-items-center">
                                        <SignUpLink
                                            clickAction={
                                                this.handleRegisterClick
                                            }
                                        />
                                    </li>
                                )}
                            </ul>
                        </nav>
                    </div>
                </header>
                {messagingModal.open === 'open' && (
                    <ModalPortal>
                        <MessagingModal
                            closeModal={closeMessagingModal}
                            {...messagingModal}
                        />
                    </ModalPortal>
                )}
                {notificationOptinModal.open === 'open' && (
                    <ModalPortal>
                        <PushNotificationModal
                            closeModal={closeNotificationOptinModal}
                            modalType={notificationOptinModal.modalType}
                            gaLabel={notificationOptinModal.gaLabel}
                            title={notificationOptinModal.title}
                            description={notificationOptinModal.description}
                            tagGroups={notificationOptinModal.tagGroups}
                        />
                    </ModalPortal>
                )}
                <TraderaToastContainer />
            </Sticky>
        );
    }
}

const mapStateToProps = state => {
    return {
        cartItemCount: state.ui.cartItemCount,
        cartSum: state.ui.cartSum,
        featureSwitches: state.environment.featureSwitches,
        hasSearch: state.ui.hasSearch,
        isLoggedIn: state.member.isLoggedIn,
        messagingModal: state.ui.messagingModal,
        notificationOptinModal: state.ui.notificationOptinModal,
        pageName: state.page.type,
        searchParams: state.discover.searchParams,
        searchUrl: state.discover.searchUrl,
        showCurrencySelection: selectShowCurrencySelection(state),
        showHeaderSearch: state.ui.showHeaderSearch,
        showSearch: state.ui.showSearchForm,
        systemMessageCount: state.ui.badges.system,
        systemMessages: state.ui.systemMessages
    };
};

const mapDispatchToProps = {
    closeCategoryDropdownMenu: uiActions.closeCategoryDropdownMenu,
    closeMessagingModal: uiActions.closeMessagingModal,
    closeNotificationOptinModal: uiActions.closeNotificationOptinModal,
    closeSearchForm: uiActions.closeSearchForm,
    fetchMessagingNotifications: uiActions.fetchMessagingNotifications,
    getGeolocation: memberStateActions.getGeolocation,
    openSearchForm: uiActions.openSearchForm
};

export default compose(
    withIsMobile,
    withIsTablet,
    withIsDesktop,
    withUrlLocalizer,
    connect(mapStateToProps, mapDispatchToProps)
)(SiteHeader);

export { SiteHeader };
